import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import landlordService from '../services/landlordService';
import FormField from '../components/common/FormField';
import Alert from '../components/common/Alert';
import { ORGANISATION_TYPES } from '../utils/propertyOptions';
import { ROLES, ROUTES } from '../utils/constants';
import './ApplicantPages.css';

const WORKING_ROLES = [ROLES.OWNER, ROLES.ADMIN, ROLES.AGENT];

// A landlord's or agent's first step: create the organisation that will own the properties.
const GetStartedPage = () => {
  const { user, userRole, mfaStatus, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: '', type: 'landlord', registration_number: '' });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Single source of truth for leaving this page once the role is a working one — whether that's
  // because they already had an organisation on arrival, or because submit() below just created one.
  // Driven by context state (not a one-off imperative call in submit) so there's no race between an
  // explicit navigate() and this component's own re-render deciding two different destinations.
  useEffect(() => {
    if (!WORKING_ROLES.includes(userRole)) return;
    if (mfaStatus === 'setup') {
      navigate('/mfa-prompt', { state: { destination: '/properties' }, replace: true });
    } else {
      navigate('/properties', { replace: true });
    }
  }, [userRole, mfaStatus, navigate]);

  const change = (e) => setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.name.trim()) {
      setErrors({ name: 'Enter your name or business name.' });
      return;
    }
    setErrors({});
    setError('');
    setBusy(true);
    try {
      await landlordService.createOrganisation({
        name: values.name.trim(),
        type: values.type,
        registration_number: values.registration_number || undefined
      });
      // Updates userRole/mfaStatus in context, which the effect above reacts to.
      await refreshProfile();
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (apiErrors) setErrors(Object.fromEntries(Object.entries(apiErrors).map(([k, v]) => [k, v[0]])));
      else setError(err.response?.data?.message || 'Your organisation could not be created.');
      setBusy(false);
    }
  };

  if (WORKING_ROLES.includes(userRole)) return null; // the effect above is navigating away

  if (user?.passwordless) {
    return (
      <div className="ap-page">
        <div className="ap-card">
          <h1>Choose a password first</h1>
          <p>
            You signed in with an emailed link, which is perfect for applying. To list properties you need a password of your
            own, so you can always log back in. We will email you a link to choose one.
          </p>
          <Link to={ROUTES.FORGOT_PASSWORD} className="btn-primary ap-button">Choose a password</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-page">
      <h1>Set up to list properties</h1>
      <p className="ap-muted">
        Tell us who you are. Next you add a property and get a link and QR code for applicants. You will also set up
        two-factor sign-in, because you will handle applicants&apos; personal information.
      </p>
      {error && <Alert type="error" message={error} />}
      <form className="ap-card" onSubmit={submit} noValidate>
        <FormField label="Your name or business name" name="name" value={values.name} onChange={change} error={errors.name} required />
        <FormField label="I am..." name="type" type="select" options={ORGANISATION_TYPES} value={values.type} onChange={change} error={errors.type} required />
        <FormField label="Company registration number (optional)" name="registration_number" value={values.registration_number} onChange={change} error={errors.registration_number} />
        <button type="submit" className="btn-primary ap-button" disabled={busy}>{busy ? 'Setting up...' : 'Continue'}</button>
      </form>
    </div>
  );
};

export default GetStartedPage;
